<script setup>
// NOTICE: KAPTION-INTEGRATION — Aggregate Campaign Reports export modal (Path 2).
// Built on Dialog.vue. Lets the user choose a format (CSV / XLSX), aggregation
// dimensions (campaign/template/status/inbox + inherited time granularity) and
// shows a read-only summary of the inherited dashboard filters (scope). On Export
// it calls the export composable, auto-downloads when ready, and renders the
// done / error / row-cap states.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useCampaignExport } from 'dashboard/composables/useCampaignExport';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

const props = defineProps({
  // v-model:open boolean control
  open: { type: Boolean, default: false },
  // The current Reports dashboard filter object:
  // { type, granularity, inboxId, campaignIds, days, includeArchived }
  dashboardFilters: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:open']);

const { t } = useI18n();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();
// feature-permissions.REPORTS.2
const canExport = computed(() => canUseFeature('campaign_exports'));

const dialogRef = ref(null);

const {
  phase,
  downloadUrl,
  errorDetail,
  isExporting,
  startAggregateExport,
  download,
  reset,
  buildAggregateFilters,
} = useCampaignExport();

// --- Format ---
const format = ref('xlsx'); // default xlsx

// --- Aggregate-by dimensions ---
const groupCampaign = ref(true); // default selected
const groupTemplate = ref(false);
const groupStatus = ref(false);
const groupInbox = ref(false);
// Time grouping is opt-in: it aggregates across all campaigns, so the worker can
// only bucket the funnel by time OR by campaign/inbox/template — never both. When
// on, it overrides those. Default off so the export is per-campaign (full context).
const groupTime = ref(false);

// Inherited time granularity (day | week | month) from the dashboard.
const granularity = computed(
  () => props.dashboardFilters?.granularity || 'day'
);

const selectedGroupBy = computed(() => {
  const out = [];
  // Time dimension is opt-in and mirrors the dashboard granularity.
  if (groupTime.value && granularity.value) out.push(granularity.value);
  if (groupCampaign.value) out.push('campaign');
  if (groupTemplate.value) out.push('template');
  if (groupStatus.value) out.push('status');
  if (groupInbox.value) out.push('inbox');
  return out;
});

const noDimensionsSelected = computed(
  () =>
    !groupCampaign.value &&
    !groupTemplate.value &&
    !groupStatus.value &&
    !groupInbox.value &&
    !groupTime.value
);

// --- Scope summary (read-only) ---
const rangeLabel = computed(() => {
  const days = props.dashboardFilters?.days;
  if (!days || days === 'all')
    return t('CAMPAIGN.REPORT.EXPORT.SCOPE_ALL_TIME');
  return t('CAMPAIGN.REPORT.EXPORT.SCOPE_LAST_DAYS', { days });
});

const inboxLabel = computed(() => {
  const id = props.dashboardFilters?.inboxId;
  if (!id || id === 'all') {
    return t('CAMPAIGN.REPORT.EXPORT.SCOPE_ALL_INBOXES');
  }
  return `#${id}`;
});

const campaignsLabel = computed(() => {
  const ids = props.dashboardFilters?.campaignIds || [];
  if (!ids.length) return t('CAMPAIGN.REPORT.EXPORT.SCOPE_ALL_CAMPAIGNS');
  return String(ids.length);
});

const includeArchivedLabel = computed(() =>
  props.dashboardFilters?.includeArchived === false
    ? t('CAMPAIGN.REPORT.EXPORT.INCLUDE_ARCHIVED_NO')
    : t('CAMPAIGN.REPORT.EXPORT.INCLUDE_ARCHIVED_YES')
);

const isCapExceeded = computed(
  () => errorDetail.value?.code === 'ROW_LIMIT_EXCEEDED'
);

// Sync external open prop with the underlying <dialog>.
watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      reset();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

const closeModal = () => {
  emit('update:open', false);
};

const handleExport = async () => {
  // feature-permissions.REPORTS.2
  if (!canExport.value) return;
  const filters = buildAggregateFilters(props.dashboardFilters);
  await startAggregateExport({
    format: format.value,
    groupBy: selectedGroupBy.value,
    filters,
  });
  if (phase.value === 'done' && downloadUrl.value) {
    download();
  }
};

defineExpose({
  open: () => emit('update:open', true),
  close: closeModal,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.REPORT.EXPORT.AGGREGATE_TITLE')"
    width="xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="closeModal"
  >
    <!-- Configure phase -->
    <div
      v-if="
        phase === 'idle' || phase === 'submitting' || phase === 'processing'
      "
      class="flex flex-col gap-5"
    >
      <!-- FORMAT -->
      <section class="flex flex-col gap-2">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CAMPAIGN.REPORT.EXPORT.FORMAT') }}
        </span>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors"
            :class="
              format === 'csv'
                ? 'bg-n-slate-12 text-n-solid-2 border-n-slate-12'
                : 'bg-transparent text-n-slate-11 border-n-weak hover:bg-n-alpha-black4'
            "
            @click="format = 'csv'"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.FORMAT_CSV') }}
          </button>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors"
            :class="
              format === 'xlsx'
                ? 'bg-n-slate-12 text-n-solid-2 border-n-slate-12'
                : 'bg-transparent text-n-slate-11 border-n-weak hover:bg-n-alpha-black4'
            "
            @click="format = 'xlsx'"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.FORMAT_XLSX') }}
          </button>
        </div>
        <p class="text-xs text-n-slate-10">
          {{
            format === 'csv'
              ? t('CAMPAIGN.REPORT.EXPORT.FORMAT_CSV_DESC')
              : t('CAMPAIGN.REPORT.EXPORT.FORMAT_XLSX_DESC')
          }}
        </p>
      </section>

      <!-- AGGREGATE BY -->
      <section class="flex flex-col gap-2">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CAMPAIGN.REPORT.EXPORT.AGGREGATE_BY') }}
        </span>
        <div class="flex flex-col gap-1.5">
          <!-- Time dimension — opt-in; overrides campaign/inbox/template -->
          <label
            class="flex items-center justify-between text-sm text-n-slate-12 px-1 cursor-pointer select-none"
          >
            <span class="flex items-center gap-2">
              <input
                v-model="groupTime"
                type="checkbox"
                class="accent-n-brand w-3.5 h-3.5"
              />
              {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_TIME') }}
            </span>
            <span class="text-xs font-medium text-n-slate-12 capitalize">
              {{ granularity }}
            </span>
          </label>
          <p
            v-if="groupTime && groupCampaign"
            class="text-xs text-n-slate-11 px-1"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_TIME_CAMPAIGN') }}
          </p>
          <p
            v-if="groupTime && (groupTemplate || groupInbox)"
            class="text-xs text-n-slate-10 px-1"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_TIME_OVERRIDES') }}
          </p>
          <label
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer select-none"
          >
            <input
              v-model="groupCampaign"
              type="checkbox"
              class="accent-n-brand w-3.5 h-3.5"
            />
            {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_CAMPAIGN') }}
          </label>
          <label
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer select-none"
          >
            <input
              v-model="groupTemplate"
              type="checkbox"
              class="accent-n-brand w-3.5 h-3.5"
            />
            {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_TEMPLATE') }}
          </label>
          <label
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer select-none"
          >
            <input
              v-model="groupStatus"
              type="checkbox"
              class="accent-n-brand w-3.5 h-3.5"
            />
            {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_STATUS') }}
          </label>
          <label
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer select-none"
          >
            <input
              v-model="groupInbox"
              type="checkbox"
              class="accent-n-brand w-3.5 h-3.5"
            />
            {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_INBOX') }}
          </label>
        </div>
        <p v-if="noDimensionsSelected" class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.REPORT.EXPORT.GROUP_NONE_HINT') }}
        </p>
      </section>

      <!-- METRICS -->
      <section class="flex flex-col gap-1">
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CAMPAIGN.REPORT.EXPORT.METRICS_LABEL') }}
        </span>
        <p class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.REPORT.EXPORT.METRICS_LIST') }}
        </p>
      </section>

      <!-- SCOPE -->
      <section
        class="flex flex-col gap-1.5 rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
      >
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CAMPAIGN.REPORT.EXPORT.SCOPE') }}
        </span>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.REPORT.EXPORT.SCOPE_RANGE') }}
          </span>
          <span class="text-n-slate-12 text-right">{{ rangeLabel }}</span>
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.REPORT.EXPORT.SCOPE_INBOX') }}
          </span>
          <span class="text-n-slate-12 text-right">{{ inboxLabel }}</span>
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.REPORT.EXPORT.SCOPE_CAMPAIGNS') }}
          </span>
          <span class="text-n-slate-12 text-right">{{ campaignsLabel }}</span>
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.REPORT.EXPORT.INCLUDE_ARCHIVED') }}
          </span>
          <span class="text-n-slate-12 text-right">
            {{ includeArchivedLabel }}
          </span>
        </div>
      </section>
    </div>

    <!-- Done phase -->
    <div
      v-else-if="phase === 'done'"
      class="flex flex-col items-center justify-center gap-3 py-8"
    >
      <span class="i-lucide-check-circle w-10 h-10 text-n-teal-9" />
      <h4 class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.EXPORT.DONE_TITLE') }}
      </h4>
      <p class="text-sm text-n-slate-11 text-center">
        {{ t('CAMPAIGN.REPORT.EXPORT.DONE_MESSAGE') }}
      </p>
      <button
        type="button"
        class="text-sm font-medium text-n-blue-text hover:underline"
        @click="download"
      >
        {{ t('CAMPAIGN.REPORT.EXPORT.DOWNLOAD_AGAIN') }}
      </button>
    </div>

    <!-- Error phase -->
    <div
      v-else-if="phase === 'error'"
      class="flex flex-col items-center justify-center gap-3 py-8"
    >
      <span class="i-lucide-alert-triangle w-10 h-10 text-n-ruby-9" />
      <h4 class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.EXPORT.ERROR_TITLE') }}
      </h4>
      <p class="text-sm text-n-slate-11 text-center max-w-sm">
        {{
          isCapExceeded
            ? t('CAMPAIGN.REPORT.EXPORT.CAP_EXCEEDED_GENERAL')
            : t('CAMPAIGN.REPORT.EXPORT.ERROR_GENERIC')
        }}
      </p>
    </div>

    <template #footer>
      <!-- Configure footer -->
      <div
        v-if="
          phase === 'idle' || phase === 'submitting' || phase === 'processing'
        "
        class="flex items-center justify-end w-full gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.REPORT.EXPORT.CANCEL')"
          type="button"
          :disabled="isExporting"
          @click="closeModal"
        />
        <!-- feature-permissions.REPORTS.2 -->
        <Button
          color="blue"
          icon="i-lucide-download"
          :label="
            isExporting
              ? t('CAMPAIGN.REPORT.EXPORT.EXPORTING')
              : t('CAMPAIGN.REPORT.EXPORT.EXPORT')
          "
          type="button"
          :is-loading="isExporting"
          :disabled="isExporting || !canExport"
          @click="handleExport"
        />
      </div>
      <!-- Done footer -->
      <div
        v-else-if="phase === 'done'"
        class="flex items-center justify-end w-full"
      >
        <Button
          color="slate"
          variant="faded"
          :label="t('CAMPAIGN.REPORT.EXPORT.CANCEL')"
          type="button"
          @click="closeModal"
        />
      </div>
      <!-- Error footer -->
      <div
        v-else-if="phase === 'error'"
        class="flex items-center justify-end w-full gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.REPORT.EXPORT.CANCEL')"
          type="button"
          @click="closeModal"
        />
        <Button
          v-if="!isCapExceeded"
          color="blue"
          :label="t('CAMPAIGN.REPORT.EXPORT.TRY_AGAIN')"
          type="button"
          @click="handleExport"
        />
      </div>
    </template>
  </Dialog>
</template>
